import { useEffect } from 'react';

interface TutorialOverlayProps {
  onDismiss: () => void;
}

const TutorialOverlay = ({ onDismiss }: TutorialOverlayProps) => {
  // Dismiss on any key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      onDismiss();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onDismiss]);

  return (
    <div
      className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
      onClick={onDismiss}
    >
      <div
        className="rounded-lg p-6 max-w-md mx-4 shadow-2xl"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className="text-xl font-bold mb-4 text-center"
          style={{ color: 'var(--text-primary)' }}
        >
          Welcome to My Career Journey
        </h3>

        <p
          className="text-sm mb-4 text-center"
          style={{ color: 'var(--text-secondary)' }}
        >
          Explore my professional history by walking through this interactive street scene.
          Each building represents a company I've worked at.
        </p>

        <div className="space-y-3 mb-6">
          <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Mouse Controls</h4>
          <div className="flex items-center gap-3 bg-blue-600 bg-opacity-20 p-3 rounded">
            <span className="text-2xl">🖱️</span>
            <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Click on any building to see job details</span>
          </div>

          <h4 className="text-sm font-semibold mt-4" style={{ color: 'var(--text-primary)' }}>Keyboard Controls</h4>
          <div
            className="flex items-center gap-3 p-3 rounded"
            style={{ backgroundColor: 'var(--bg-primary)' }}
          >
            <div className="flex gap-1">
              <kbd className="px-2 py-1 rounded text-sm font-mono" style={{ backgroundColor: 'var(--border-color)', color: 'var(--text-primary)' }}>&larr;</kbd>
              <kbd className="px-2 py-1 rounded text-sm font-mono" style={{ backgroundColor: 'var(--border-color)', color: 'var(--text-primary)' }}>&rarr;</kbd>
            </div>
            <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Walk left and right</span>
          </div>

          <div
            className="flex items-center gap-3 p-3 rounded"
            style={{ backgroundColor: 'var(--bg-primary)' }}
          >
            <kbd className="px-2 py-1 rounded text-sm font-mono" style={{ backgroundColor: 'var(--border-color)', color: 'var(--text-primary)' }}>&uarr;</kbd>
            <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Enter building above the road</span>
          </div>

          <div
            className="flex items-center gap-3 p-3 rounded"
            style={{ backgroundColor: 'var(--bg-primary)' }}
          >
            <kbd className="px-2 py-1 rounded text-sm font-mono" style={{ backgroundColor: 'var(--border-color)', color: 'var(--text-primary)' }}>&darr;</kbd>
            <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Enter building below the road</span>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={onDismiss}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Start Exploring
          </button>
        </div>

        <p className="text-xs text-center mt-4" style={{ color: 'var(--text-secondary)' }}>
          Press any key or click to dismiss
        </p>
      </div>
    </div>
  );
};

export default TutorialOverlay;
